import { msalConfig } from "src/auth/Managers/Microsoft/msalConfig";
import { PublicClientApplication } from "@azure/msal-browser";
import AuthManager from "src/auth/AuthManager";

declare global {
  interface Window {
    msalClient: PublicClientApplication;
  }
}
export class MicrosoftAuthManager {
  private msalClient: PublicClientApplication | null = null;
  private _initializationPromise: Promise<void> | null = null;
  private _isInitialized: boolean = false;
  private authManager: AuthManager;
  public sharepointBaseAddress: string | null = null;
  public sharepointToken: string | null = null;
  public graphToken: string | null = null;

  constructor(authManager: AuthManager) {
    this.authManager = authManager;
    try {
      this.msalClient = new PublicClientApplication(msalConfig);
      // Create the initialization promise
      this._initializationPromise = this.initialize();
    } catch (error) {
      console.error("Failed to initialize MicrosoftAuthManager", error);
    }
  }

  private async initialize(): Promise<void> {
    try {
      await this.msalClient?.initialize();
      const result = await this.msalClient?.handleRedirectPromise();
      if (result) {
        this.authManager.token = result.idToken;
        this.authManager.isAuthenticated = true;
        this.authManager.type = "microsoft";
        this.authManager.providerLoginResult = result;
        this.authManager.providerLoginResult.exp = (
          result.idTokenClaims as any
        ).exp;
        this.authManager.name = result.account?.name ?? null;
        this.authManager.email = result.account?.username ?? null;
        this.authManager.notifyListeners();
      }

      this._isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize MicrosoftAuthManager:", error);
      this._isInitialized = false;
      throw error;
    }
  }

  /**
   * Ensures the manager is initialized before proceeding
   */
  private async ensureInitialized(): Promise<void> {
    if (!this._isInitialized) {
      await this._initializationPromise;
    }
  }

  /**
   * Returns a promise that resolves when initialization is complete
   */
  public get initializationPromise(): Promise<void> {
    return this._initializationPromise as Promise<void>;
  }

  /**
   * Returns whether the manager has been initialized
   */
  public get isInitialized(): boolean {
    return this._isInitialized;
  }

  public async signOut() {
    await this.ensureInitialized();

    this.authManager.token = null;
    this.authManager.type = "microsoft";
    this.authManager.providerLoginResult = null;
    this.authManager.isAuthenticated = false;
    this.authManager.name = null;
    this.authManager.email = null;

    localStorage.removeItem("auth");
    this.msalClient?.clearCache();
    this.authManager.notifyListeners();
    this.authManager.logoutCallback();
  }

  public async fetch_sharepoint_token() {
    await this.ensureInitialized();

    const result = await this.msalClient?.acquireTokenSilent({
      scopes: [`https://${this.sharepointBaseAddress}/.default`],
    });
    this.sharepointToken = result?.accessToken ?? null;
    return result?.accessToken ?? null;
  }

  public async fetch_graph_token() {
    await this.ensureInitialized();

    const result = await this.msalClient?.acquireTokenSilent({
      scopes: ["https://graph.microsoft.com/.default"],
      authority: msalConfig.auth.authority,
      prompt: "select_account",
    });
    this.graphToken = result?.accessToken ?? null;
    return result?.accessToken ?? null;
  }

  public async getSharepointBaseAddress() {
    await this.ensureInitialized();

    if (!this.graphToken) {
      await this.fetch_graph_token();
    }
    const response = await fetch(
      "https://graph.microsoft.com/v1.0/sites/root",
      {
        headers: {
          Authorization: `Bearer ${this.graphToken}`,
        },
      }
    );
    const data = await response.json();
    return data.siteCollection.hostname;
  }

  public async signIn() {
    await this.ensureInitialized();

    this.authManager.token = null;
    this.authManager.isAuthenticated = false;
    this.authManager.type = "microsoft";
    this.authManager.providerLoginResult = null;

    this.msalClient?.acquireTokenRedirect({
      scopes: ["offline_access", "openid", "email"],
      prompt: "select_account",
    });
  }

  public async signInSilent() {
    await this.ensureInitialized();
    if (
      ((!this.msalClient?.getActiveAccount() &&
        (this.msalClient?.getAllAccounts()?.length || 0)) ||
        0) > 0
    ) {
      this.msalClient?.setActiveAccount(this.msalClient?.getAllAccounts()[0]);
    }

    if (!this.msalClient?.getActiveAccount()) {
      // If no active account, we need to sign in first
      await this.signIn();
      // After sign in, we need to fetch the Graph token first
      await this.fetch_graph_token();
    } else {
      // If there's an active account, try to get tokens silently
      try {
        await this.fetch_graph_token();
      } catch (error) {
        // If silent token acquisition fails, fall back to interactive sign in

        await this.signIn();
        await this.fetch_graph_token();
      }
    }

    // Now we can get the SharePoint base address and token
    const sharepointBaseAddress = await this.getSharepointBaseAddress();
    this.sharepointBaseAddress = sharepointBaseAddress;
    await this.fetch_sharepoint_token();
    this.authManager.isAuthenticated = true;
    this.authManager.notifyListeners();
  }

  public async trySignInSilently() {
    await this.ensureInitialized();

    const token_to_refresh = this.authManager.token;
    if (!token_to_refresh) {
      return false;
    }
    this.msalClient?.setActiveAccount(this.msalClient?.getAllAccounts()[0]);
    const accessToken = await this.msalClient
      ?.acquireTokenSilent({ scopes: ["offline_access", "openid", "email"] })
      .then((result) => {
        this.authManager.token = result.idToken;
        this.authManager.isAuthenticated = true;
        this.authManager.type = "microsoft";
        this.authManager.providerLoginResult = result;
        this.authManager.notifyListeners();
        this.authManager.email = result.account?.username ?? null;
        return result?.idToken ?? null;
      })
      .catch((error) => {
        console.error("Error from MS Refresh, ", error);
      });
    if (accessToken) {
      return !!accessToken;
    }
  }
}
